import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, map } from 'rxjs';
import { LoginResponse } from '../models/auth-state.model';
import { User } from '../models/user.model';
import { LoginRepository } from '../repositories/login.repository';
import { AuthStateService } from './auth-state.service';
import { ProductsService } from './products.service';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  constructor(
    private router: Router,
    private loginRepositoryHttp: LoginRepository,
    private authState: AuthStateService,
    private productsService: ProductsService,
  ) {}

  /**
   * Logs the user into UI
   *
   * @param username Username
   * @param password Password
   * @returns User login response stream
   */
  login(username: string, password: string): Observable<LoginResponse> {
    return this.loginRepositoryHttp.login(username, password).pipe(
      map((response) => {
        if (!response.body) {
          throw new Error('Empty login response');
        }
        return response.body;
      }),
    );
  }

  isTokenValid(): boolean {
    if (this.authState.isAuthenticated()) {
      return true;
    }
    // SSO authentication: session is managed via an HttpOnly cookie on the backend.
    // The frontend tracks this via a sessionStorage marker set after the OAuth2 callback.
    const hasSsoMarker = sessionStorage.getItem('ssoSession') === '1';
    if (!hasSsoMarker) {
      return false;
    }
    // Avoid treating stale partial state as authenticated (can cause access-denied loops).
    const state = this.authState.load();
    return !!state?.username;
  }

  /**
   * Returns the token if the token is valid
   * @returns string
   */
  getToken(): string | undefined {
    return this.isTokenValid() ? localStorage.getItem('token') || '' : undefined;
  }

  setUser(user: User) {
    localStorage.setItem('userDetails', JSON.stringify(user));
  }

  getUser(): User {
    const user = localStorage.getItem('userDetails');
    return user ? JSON.parse(user) : null;
  }

  async logout(token?: string): Promise<boolean> {
    const isSsoSession = sessionStorage.getItem('ssoSession') === '1';
    if (!isSsoSession) {
      const tokenToUse = token ?? this.authState.token ?? null;
      if (tokenToUse) {
        try {
          await firstValueFrom(this.loginRepositoryHttp.logout(tokenToUse));
        } catch (error) {
          console.error(error);
        }
      }
    }
    this.authState.clear();
    // Clear in-memory product state so the next user doesn't inherit this session's form inputs
    // and selections (the service is a root singleton that survives the SPA logout navigation).
    this.productsService.clearAllState();
    return this.router.navigateByUrl('/login');
  }
}
